import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Download, GitFork, RefreshCw, Search } from 'lucide-react'
import { Link } from 'react-router-dom'
import { exportOpenPoToExcel } from './openPoExcel'
import { listSuppliersApi } from '@/services/suppliers/apiClient'
import type { Supplier } from '@/services/suppliers'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { PageHeader } from '@/components/layout/PageHeader'
import { KpiCard } from '@/components/layout/KpiCard'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { EmptyState } from '@/components/ui/EmptyState'
import { PageError, PageLoading } from '@/components/ui/PageAsyncState'
import { formatDateFull } from '@/lib/dateFormat'
import { formatVnNumber } from '@/features/purchasing/poWorkflow'
import { PoLineTraceabilityModal } from '@/features/purchasing/PoLineTraceabilityModal'
import {
  getPoLineTraceabilityApi,
  type PoLineTraceabilityResult,
} from '@/services/procurement/materialRequirementApiClient'
import {
  fetchOpenPurchaseOrdersReportApi,
  type OpenPoReport,
  type PoDeliveryStatus,
} from '@/services/reports/reportsApi'

const DELIVERY_LABEL: Record<PoDeliveryStatus, string> = {
  NOT_RECEIVED: 'Chưa nhận',
  PARTIALLY_RECEIVED: 'Giao một phần',
  FULLY_RECEIVED: 'Đã nhận đủ',
}

const DELIVERY_TONE: Record<PoDeliveryStatus, 'neutral' | 'warning' | 'success'> = {
  NOT_RECEIVED: 'neutral',
  PARTIALLY_RECEIVED: 'warning',
  FULLY_RECEIVED: 'success',
}

const WORKFLOW_LABEL: Record<string, string> = {
  APPROVED: 'Đã duyệt',
  SENT: 'Đã gửi NCC',
  PARTIAL: 'Đang nhận hàng',
  SHORT: 'Thiếu hàng',
}

export interface OpenPurchaseOrdersReportPageProps {
  initialReport?: OpenPoReport
}

export function OpenPurchaseOrdersReportPage({
  initialReport,
}: OpenPurchaseOrdersReportPageProps = {}): React.ReactElement {
  const [report, setReport] = useState<OpenPoReport | null>(initialReport ?? null)
  const [loading, setLoading] = useState(!initialReport)
  const [error, setError] = useState<string | null>(null)
  const [qInput, setQInput] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [overdueOnly, setOverdueOnly] = useState(false)
  const [includeFullyReceived, setIncludeFullyReceived] = useState(false)
  const [selectedSupplierId, setSelectedSupplierId] = useState('')
  const [suppliers, setSuppliers] = useState<Supplier[]>([])

  useEffect(() => {
    listSuppliersApi().then(setSuppliers).catch(() => {})
  }, [])
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const [traceOpen, setTraceOpen] = useState(false)
  const [traceData, setTraceData] = useState<PoLineTraceabilityResult | null>(null)
  const [traceLoading, setTraceLoading] = useState(false)
  const [traceError, setTraceError] = useState<string | null>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQ(qInput), 250)
    return () => window.clearTimeout(timer)
  }, [qInput])

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setReport(
        await fetchOpenPurchaseOrdersReportApi({
          q: debouncedQ,
          overdueOnly,
          supplierId: selectedSupplierId || undefined,
          includeFullyReceived,
        }),
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải báo cáo PO đang mở')
    } finally {
      setLoading(false)
    }
  }, [debouncedQ, overdueOnly, selectedSupplierId, includeFullyReceived])

  useEffect(() => {
    if (initialReport && !debouncedQ && !overdueOnly && !selectedSupplierId && !includeFullyReceived) return
    void loadData()
  }, [loadData, initialReport, debouncedQ, overdueOnly, selectedSupplierId, includeFullyReceived])

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function openTraceability(poLineId: string) {
    setTraceOpen(true)
    setTraceLoading(true)
    setTraceError(null)
    setTraceData(null)
    try {
      setTraceData(await getPoLineTraceabilityApi(poLineId))
    } catch (err) {
      setTraceError(err instanceof Error ? err.message : 'Không thể tải nguồn nhu cầu của dòng PO')
    } finally {
      setTraceLoading(false)
    }
  }

  const rawItems = report?.items ?? []
  const items = useMemo(() => {
    if (includeFullyReceived) return rawItems
    return rawItems.filter((i) => i.delivery_status !== 'FULLY_RECEIVED')
  }, [rawItems, includeFullyReceived])

  const summary = useMemo(() => {
    if (!report?.summary) return undefined
    if (includeFullyReceived) return report.summary
    return {
      po_count: items.length,
      overdue_count: items.filter((i) => i.is_overdue).length,
      not_received_count: items.filter((i) => i.delivery_status === 'NOT_RECEIVED').length,
      partially_received_count: items.filter((i) => i.delivery_status === 'PARTIALLY_RECEIVED').length,
      total_amount: Math.round(items.reduce((s, i) => s + i.total_amount, 0) * 10000) / 10000,
    }
  }, [report?.summary, items, includeFullyReceived])

  const supplierOptions = useMemo(() => {
    const map = new Map<string, string>()
    for (const s of suppliers) {
      if (s.id) map.set(s.id, s.name)
    }
    for (const item of rawItems) {
      if (item.supplier_id && !map.has(item.supplier_id)) {
        map.set(item.supplier_id, item.supplier_name)
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }))
  }, [suppliers, rawItems])


  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Báo cáo', to: '/reports' },
          { label: 'PO đang mở' },
        ]}
      />
      <PageHeader
        title="PO đang mở & tiến độ giao hàng"
        subtitle="Mỗi PO gồm nhiều dòng hàng; bấm vào PO để xem từng dòng: đã đặt, đã nhận, còn lại."
        actions={
          <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => void loadData()} disabled={loading}>
            <RefreshCw className="mr-1.5 h-4 w-4" />
            Làm mới
            </Button>
            <Button
              variant="secondary"
              onClick={() => exportOpenPoToExcel(items)}
              disabled={loading || items.length === 0}
              title="Xuất báo cáo PO đang mở ra file Excel (.xlsx)"
            >
              <Download className="mr-1.5 h-4 w-4" />
              Xuất Excel
            </Button>
          </div>
        }
      />

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="PO đang mở" value={summary?.po_count ?? 0} />
        <KpiCard
          label="Quá hạn giao"
          value={summary?.overdue_count ?? 0}
          tone={summary && summary.overdue_count > 0 ? 'danger' : 'default'}
        />
        <KpiCard label="Giao một phần" value={summary?.partially_received_count ?? 0} tone="warning" />
        <KpiCard
          label="Tổng tiền (chưa VAT)"
          value={`${formatVnNumber(summary?.total_amount ?? 0)} ₫`}
        />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-72 max-w-full">
          <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" />
          <input
            type="search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Tìm theo mã PO hoặc nhà cung cấp"
            aria-label="Tìm PO"
            className="h-9 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] pr-3 pl-9 text-sm"
          />
        </div>
        <select
          value={selectedSupplierId}
          onChange={(e) => setSelectedSupplierId(e.target.value)}
          aria-label="Lọc theo nhà cung cấp"
          className="h-9 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm text-[var(--color-text)]"
        >
          <option value="">Tất cả nhà cung cấp</option>
          {supplierOptions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-[var(--color-text)] cursor-pointer">
          <input
            type="checkbox"
            checked={overdueOnly}
            onChange={(e) => setOverdueOnly(e.target.checked)}
          />
          Chỉ PO quá hạn
        </label>
        <label className="flex items-center gap-2 text-sm text-[var(--color-text)] cursor-pointer">
          <input
            type="checkbox"
            checked={includeFullyReceived}
            onChange={(e) => setIncludeFullyReceived(e.target.checked)}
          />
          Hiện cả PO đã nhận đủ
        </label>
      </div>

      {loading && !report ? (
        <PageLoading />
      ) : error ? (
        <PageError message={error} onRetry={() => void loadData()} />
      ) : items.length === 0 ? (
        <EmptyState
          title="Không có PO đang mở"
          description="Không có đơn mua nào đang chờ hàng theo bộ lọc hiện tại."
        />
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)]">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold text-[var(--color-text-muted)] uppercase">
              <tr>
                <th className="w-12 px-3 py-2">STT</th>
                <th className="px-3 py-2 whitespace-nowrap min-w-[120px]">Mã PO</th>
                <th className="px-3 py-2">Nhà cung cấp</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Số dòng hàng</th>
                <th className="px-3 py-2 text-right whitespace-nowrap min-w-[130px]">Tổng tiền</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">% Đã nhận</th>
                <th className="px-3 py-2 whitespace-nowrap">Dự kiến giao</th>
                <th className="px-3 py-2 whitespace-nowrap">Tình trạng giao</th>
                <th className="px-3 py-2 whitespace-nowrap">Trạng thái PO</th>
              </tr>
            </thead>
            <tbody>
              {items.map((po, idx) => {
                const isOpen = expanded.has(po.id)
                return (
                  <Fragment key={po.id}>
                    <tr
                      className="cursor-pointer border-t border-[var(--color-border)] hover:bg-slate-50"
                      onClick={() => toggle(po.id)}
                      data-testid={`po-row-${po.code}`}
                    >
                      <td className="px-3 py-2 tabular-nums">{idx + 1}</td>
                      <td className="px-3 py-2 font-medium whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          {isOpen ? (
                            <ChevronDown className="h-4 w-4" aria-hidden />
                          ) : (
                            <ChevronRight className="h-4 w-4" aria-hidden />
                          )}
                          <Link
                            to={`/purchasing/po/${po.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 hover:underline font-semibold"
                            title="Mở đơn mua hàng"
                          >
                            {po.code}
                          </Link>
                        </span>
                      </td>
                      <td className="px-3 py-2">{po.supplier_name}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{po.line_count}</td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                        {formatVnNumber(po.total_amount)} ₫
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{po.received_pct}%</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {formatDateFull(po.delivery_date)}
                        {po.is_overdue ? (
                          <span className="ml-2">
                            <StatusBadge tone="danger" label="Quá hạn" />
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <StatusBadge
                          tone={DELIVERY_TONE[po.delivery_status]}
                          label={DELIVERY_LABEL[po.delivery_status]}
                        />
                      </td>
                      <td className="px-3 py-2 text-[var(--color-text-muted)] whitespace-nowrap">
                        {WORKFLOW_LABEL[po.workflow_status] ?? po.workflow_status}
                      </td>
                    </tr>
                    {isOpen ? (
                      <tr className="bg-slate-50/60">
                        <td />
                        <td colSpan={8} className="px-3 py-3">
                          <table className="w-full text-sm" data-testid={`po-lines-${po.code}`}>
                            <thead className="text-left text-xs font-semibold text-[var(--color-text-muted)] uppercase">
                              <tr>
                                <th className="w-12 px-2 py-1">STT</th>
                                <th className="px-2 py-1">Vật tư</th>
                                <th className="px-2 py-1 text-right">Đã đặt</th>
                                <th className="px-2 py-1 text-right">Đã nhận</th>
                                <th className="px-2 py-1 text-right">Còn lại</th>
                                <th className="px-2 py-1">ĐVT</th>
                                <th className="px-2 py-1">Giao hàng</th>
                                <th className="px-2 py-1">Nguồn nhu cầu</th>
                              </tr>
                            </thead>
                            <tbody>
                              {po.lines.map((line, lineIdx) => (
                                <tr key={line.id} className="border-t border-[var(--color-border)]">
                                  <td className="px-2 py-1 tabular-nums">{lineIdx + 1}</td>
                                  <td className="px-2 py-1">
                                    <div className="font-medium">{line.material_name}</div>
                                    {line.spec_text ? (
                                      <div className="text-xs text-[var(--color-text-muted)]">
                                        {line.spec_text}
                                      </div>
                                    ) : null}
                                  </td>
                                  <td className="px-2 py-1 text-right tabular-nums">
                                    {formatVnNumber(line.ordered_qty)}
                                  </td>
                                  <td className="px-2 py-1 text-right tabular-nums">
                                    {formatVnNumber(line.received_qty)}
                                  </td>
                                  <td className="px-2 py-1 text-right font-semibold tabular-nums">
                                    {formatVnNumber(line.remaining_qty)}
                                  </td>
                                  <td className="px-2 py-1">{line.order_uom}</td>
                                  <td className="px-2 py-1">
                                    <StatusBadge
                                      tone={DELIVERY_TONE[line.delivery_status]}
                                      label={DELIVERY_LABEL[line.delivery_status]}
                                    />
                                  </td>
                                  <td className="px-2 py-1">
                                    {line.requirement_line_id ? (
                                      <Button
                                        variant="secondary"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          void openTraceability(line.id)
                                        }}
                                      >
                                        <GitFork className="mr-1 h-3.5 w-3.5" />
                                        Xem nguồn
                                      </Button>
                                    ) : (
                                      <span className="text-xs text-[var(--color-text-muted)]">—</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <PoLineTraceabilityModal
        isOpen={traceOpen}
        onClose={() => setTraceOpen(false)}
        data={traceData}
        loading={traceLoading}
        error={traceError}
      />
    </div>
  )
}
